import type {
  CartCheckoutPanelProps,
  CartCheckoutRowAction,
} from '@engyalo/delivery-ui-components'
import type { SessionCheckoutRule } from '../graphQL/queries/getSession/types'
import {
  CheckoutRules,
  type CheckoutRuleDocumentType,
  type UnresolvedRecord,
} from '../interfaces'
import type { ICinnamonActions } from '../interfaces/actions'
import {
  CinnamonActionNames,
  TextLibrary,
} from '../consts/defaultConfigValues/defaultConstants'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'
import { readSessionRulePick, selectRulePick } from './checkoutRuleSelection'
import { resolveCheckoutRuleRoute } from './checkoutRuleRoute'
import type { AutoSettleStatus } from '../redux/slices/checkout/types'

const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined

/**
 * The panel props this adapter owns, checked against the library's own
 * prop shape.
 *
 * The same guard `getOrderConfirmationPanelProps` uses one level up, and it
 * catches the same class of drift: the injector is registered in a `Map`
 * whose value type is `UnresolvedRecord`, so a library rename of
 * `expectedDeliveryDate` or `paymentMethod` would compile, land unread on the
 * panel, and silently drop the row.
 *
 * **It guards names and value types, not presence.** Every panel prop is
 * optional, so `{}` satisfies this type — dropping a line from the returned
 * object is a compile *success*. That case is covered by the colocated
 * assertions instead, which is why each forwarded prop has a test naming it.
 */
type InjectedCartCheckoutProps = Pick<
  CartCheckoutPanelProps,
  'expectedDeliveryDate' | 'paymentMethod' | 'hidePrices'
> & {
  deliveryAction?: InjectedRowAction
  paymentAction?: InjectedRowAction
}

/**
 * A row action as this adapter emits it, before the config pipeline resolves
 * it.
 *
 * The library declares `onClick: () => void`, and that is what the panel
 * receives — but not what this file returns. An injector is a plain function
 * with no `navigate` and no `dispatch`, so it cannot build the closure. What
 * it can build is the authored-action shape, which `processActions` converts
 * into a real function one step later.
 *
 * `Omit` keeps `label`, `disabled` and `'data-testid'` tied to the library's
 * own declaration, so a rename there fails this build.
 */
type InjectedRowAction = Omit<CartCheckoutRowAction, 'onClick'> & {
  /** Resolved into `() => void` by `processActions`. See `buildRowAction`. */
  onClick: ICinnamonActions
}

/**
 * Both action props still exist on the library's prop type.
 *
 * `InjectedRowAction` derives from `CartCheckoutRowAction`, so a rename of
 * `label` fails on its own. The prop *names* are the gap: they are literals
 * above, and a library that renamed `deliveryAction` would leave this file
 * compiling and the button unrendered. Type-only, so nothing reaches the
 * bundle — the `SessionRuleTypeMatchesConfig` idiom from `getSession/types.ts`.
 */
type PanelActionKeysExist = 'deliveryAction' | 'paymentAction' extends
  keyof CartCheckoutPanelProps
  ? true
  : never
const panelActionKeysExist: PanelActionKeysExist = true
void panelActionKeysExist

/**
 * The pointwise mapper: the session's selection array collapsed to the two
 * scalars the panel actually reads.
 *
 * `null` or empty in, `undefined` out — the panel's own two props are
 * optional and it drops each row for an absent value (COM-852), so returning
 * `undefined` here is the same story one level down. That is the pilot's
 * normal state until the write path exists (OQ-7 on COM-849).
 *
 * `setCheckoutRuleById` filters and appends, so the array holds at most one
 * entry per `ruleType`. `find` reflects that. A second entry for the same
 * type would be silently ignored — which is the right shape for a defensive
 * read: cinnamon has no authority to reject a shape the server accepted, and
 * picking either would be a coin toss.
 *
 * **Nothing is normalised here.** The delivery `data` is a `YYYY-MM-DD`
 * calendar-day string and reaches the panel raw — `CartCheckoutPanel` uses
 * `formatOrderCalendarDay` which pins the date to UTC, and applying a
 * viewer's zone to it moves it west of UTC to the previous day (the COM-816
 * defect rather than its fix). The payment `data` is a raw backend enum
 * value the panel maps through `paymentMethodLabels`, authored per
 * storefront — cinnamon does not translate either.
 *
 * ⚠️ The array's `value` is a nullable `SessionCheckoutRuleValue`, so the
 * chain is `?.value?.data` rather than `.value.data`. A rule entry the
 * storefront wrote with no `value` yet reaches here as `{ ruleType, value:
 * null }`, and reading `.value.data` on it throws.
 */
export const toCartCheckoutPanelSelection = (
  rules?: SessionCheckoutRule[] | null,
): Pick<
  InjectedCartCheckoutProps,
  'expectedDeliveryDate' | 'paymentMethod'
> | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  return {
    expectedDeliveryDate: readSessionRulePick(
      rules,
      CheckoutRules.DELIVERY_DATES,
    ),
    paymentMethod: readSessionRulePick(rules, CheckoutRules.PAYMENT_METHODS),
    // `hidePrices` belongs to the injector, not the pointwise mapper — the
    // mapper only knows the rules array. Left absent here so the injector
    // spreads it in from `RootState` below.
  }
}

/**
 * A path that leaves this origin. `//host` and `/\host` are both
 * protocol-relative to a browser.
 */
const ESCAPES_ORIGIN = /^\/[/\\]/

/**
 * Auto-settle outcomes that leave the shopper nothing to change.
 *
 * COM-1048's half of this control, and the seam COM-1045 left for it.
 *
 * `'settled'` is the point of that ticket: the rule offered one value and the
 * storefront took it, so a *Cambiar* button would send the shopper to a screen
 * with a single option already chosen. `'empty'` offered nothing at all.
 * `'resolving'` and `'writing'` are the window before the answer is known — the
 * footer CTA is held across exactly the same window, so withholding this
 * control beside it keeps the screen saying one thing.
 *
 * Every other outcome keeps today's behaviour, and the defaults matter more
 * than the suppressions:
 *
 * - **absent** — nothing decided. A storefront whose cart container declares
 *   no `type: 'cart'` never mounts the hook at all, and suppressing on absence
 *   would silently delete its *Cambiar* buttons. That is the read-only M1-C
 *   storefront COM-1045 regression-guards.
 * - `'multiple'` — the shopper picks, which is what the control is for.
 * - `'failed'` and `'unavailable'` — the rule is unsettled and the picker is
 *   the fallback, which is why COM-1048 keeps the route authored rather than
 *   treating it as dead config.
 */
const SUPPRESSES_CHANGE: AutoSettleStatus[] = [
  'resolving',
  'writing',
  'settled',
  'empty',
]

/**
 * The **Cambiar** control for one row, or nothing.
 *
 * Both halves ride the config pipeline that runs after injection, and neither
 * is a value this function could produce on its own:
 *
 * - `label` is emitted as its `TextLibrary` token. `processData` recurses into
 *   this object and resolves it against `sources.texts` →
 *   `state.defaultSlice.config.texts`, so a storefront that overrides the copy
 *   in its own SCS body gets its own word.
 * - `onClick` is emitted as an authored action. `processActions` recurses,
 *   finds `cinnamonActions`, and replaces the object with a real function
 *   through `createFunction`. `PUSH_VIEW` is already registered on the emitter
 *   in `containers/EventManager.tsx`, so this needs no new action name and no
 *   new handler.
 *
 * ⚠️ **Emit both or neither.** The panel re-checks both at runtime —
 * `typeof label === 'string' && !!label && typeof onClick === 'function'` —
 * because these props reach it from untyped SCS config. A label with no route
 * would draw nothing, which is the right outcome by luck rather than by
 * intent; emitting neither is what the read-only storefront asks for.
 *
 * ⚠️ **The origin guard is this call site's job, not the resolver's.**
 * `resolveCheckoutRuleRoute` leaves containment to its caller and says so: on
 * COM-950's path that containment is `navigate(path, { replace: true })`,
 * because `history.replace` calls `replaceState` bare and the browser's
 * same-origin check throws. This control is a **push** — the shopper picks and
 * comes back, so `replace` would eat the summary they came from — and
 * `history.push` swallows that rejection and falls through to
 * `window.location.assign`, which navigates cross-origin. `storeName` is read
 * out of the URL, so a crafted `%2f%2fevil.com` reaches here as `//evil.com`.
 * The resolver's `matchRoutes` gate rejects that shape today only by accident
 * of segment count, and a storefront authoring a splat route would reopen it.
 */
const buildRowAction = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
  label: TextLibrary,
): InjectedRowAction | undefined => {
  /*
   * Optional all the way down, matching `selectRulePick` beside it.
   * `checkoutSlice` is registered on the real store and cannot be absent at
   * runtime, but a `RootState` in this repo is routinely a partial object cast
   * into shape by a test fixture — this file's own suite supplies
   * `defaultSlice` and nothing else — and a non-optional read there throws
   * rather than answering.
   */
  const settledBy = state.checkoutSlice?.autoSettle?.[ruleType]
  if (settledBy && SUPPRESSES_CHANGE.includes(settledBy)) return undefined

  const path = resolveCheckoutRuleRoute(
    state.defaultSlice.config?.checkoutRuleRoutes?.[ruleType],
    state.defaultSlice.config?.routes,
    state.defaultSlice.storeName,
    state.defaultSlice.sessionId,
  )
  if (!path || ESCAPES_ORIGIN.test(path)) return undefined

  return {
    label,
    onClick: {
      cinnamonActions: [
        { name: CinnamonActionNames.PUSH_VIEW, args: { path } },
      ],
    },
  }
}

/**
 * The three `CartCheckoutPanel` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `CartCheckoutPanel`, exactly as `getOrderConfirmationPanelProps` is for
 * the confirmation screen. None of these is authorable as a `{{variable}}`:
 * two are derived from the session's selections (a shape the config DSL
 * cannot build) and one — `hidePrices` — is derived from a state predicate
 * the config DSL cannot compute.
 *
 * Everything else stays in config — every label, the helper lines, the
 * icons, the icon set, the currency, the payment-method label map, the
 * colour props, `fieldsToDisplay` and the custom slots. This file adds no
 * layout or copy decision.
 *
 * Injected **before** `processData` and `maskPricesForDisplay` run, so slot
 * text is masked with everything else — same seam as `orderDetailPanel` and
 * `orderConfirmationPanel`.
 *
 * **No `timeZone` on this panel.** `CartCheckoutPanel` uses
 * `formatOrderCalendarDay` for the delivery date, which pins to UTC on
 * purpose; the panel has no `timeZone` prop at all (COM-859). That is
 * deliberate rather than an oversight — see `toCartCheckoutPanelSelection`
 * for why the reverse ships the COM-816 defect.
 *
 * **No `locale` either.** The library falls back to the runtime default when
 * the prop is absent, and the tenant's locale is already authorable as
 * `{{config.options.language}}` or a text token — cinnamon does not know one
 * that is not on the config.
 */
export const getCartCheckoutPanelProps = (
  state: RootState,
  props: UnresolvedRecord = {},
): InjectedCartCheckoutProps => {
  const base: InjectedCartCheckoutProps = {
    expectedDeliveryDate: optional(
      selectRulePick(state, CheckoutRules.DELIVERY_DATES),
    ),
    paymentMethod: optional(
      selectRulePick(state, CheckoutRules.PAYMENT_METHODS),
    ),
    /**
     * Slot text is a string, and `maskPricesForDisplay` is an allowlist of
     * prop names over numbers, so it cannot see a price inside a slot's
     * `'Precio por kilo $98.90'`. This panel renders no money of its own —
     * a delivery date and a payment *method* are not amounts — but the two
     * custom slots can reach any field on the shopper's session, and the
     * flag is what stops one from leaking a total.
     */
    hidePrices: isHidePricesEnabled(state),
  }

  /*
   * The summary placement repeats the fact and owns no affordance.
   *
   * The library ignores both action props on `variant='summary'`, so this
   * branch changes no pixel. It exists so cinnamon does not *send* a control
   * it means to withhold: two **Cambiar** buttons for one value on one screen
   * leave the shopper asking whether they do the same thing, and the card
   * above the item list is the one that answers.
   */
  if (props.variant === 'summary') return base

  const deliveryAction = buildRowAction(
    state,
    CheckoutRules.DELIVERY_DATES,
    TextLibrary.DELIVERY_DATE_CHANGE_LABEL,
  )
  const paymentAction = buildRowAction(
    state,
    CheckoutRules.PAYMENT_METHODS,
    TextLibrary.PAYMENT_METHOD_CHANGE_LABEL,
  )

  /*
   * The key is absent rather than `undefined` when a rule offers no change.
   *
   * Either reads the same to the panel. Absence is what the read-only
   * storefront's regression guard asserts, and an explicit `undefined` spread
   * over config would be this adapter claiming a prop it has decided not to
   * own.
   */
  return {
    ...base,
    ...(deliveryAction ? { deliveryAction } : {}),
    ...(paymentAction ? { paymentAction } : {}),
  }
}

export default getCartCheckoutPanelProps
